<script>
import {formatZonedDateTime} from "@/baseUtils";
import {getImportanceItem} from "@/utils/OpinionUtils";
import i18n from "@/i18n";
import BaseAvatar from "@/components/BaseAvatar/BaseAvatar.vue";

export default {
  name: "OpinionListItem",
  props: {
    itemData: {
      type: Object
    }
  },
  components: {
    BaseAvatar
  },
  computed: {
    itemTitle() {
      const currentProposal = this.itemData.proposalVersions[0];

      return currentProposal.header;
    },
    itemDateLabel() {
      return this.itemData.version > 1 ? i18n.global.t('mainSearch.updated') : i18n.global.t('mainSearch.registered');
    },
    opinionUpdateDate() {
      return formatZonedDateTime(this.itemData.updatedDateTime);
    },
    importanceTypeText() {
      const item = getImportanceItem(this.itemData.importanceType);
      return item.index;
    }
  },
  methods: {
    activeOpinionLikeType(type) {
      return this.itemData.opinionLike === type ? 'active' : '';
    }
  }
}
</script>

<template>
  <div class="opinion-list-item">
    <router-link :to="{name: 'OpinionView', params: {id: itemData.id}}"
                 target="_blank"
                 class="opinion-header"
    >{{itemTitle}}</router-link>
    <div class="info-row mb-15">
      <span class="info-text">{{itemDateLabel}} {{opinionUpdateDate}}</span>
    </div>
    <div class="info-row mb-15">
      <span class="info-label">{{$t('mainSearch.opinion.opinionFrom')}}</span>
      <span class="info-text">{{itemData.creator.name}}</span>
      <base-avatar  :size="'SM'"
                    class=""
                    :img-hash="itemData.creator.largeImgHash"
      />
    </div>
    <div class="opinion-info">
      <ul class="opinion-reaction-list">
        <li class="opinion-reaction-item opinion-reaction-item--very-good"
            :class="activeOpinionLikeType('VERY_GOOD')"
        >
          <div class="opinion-reaction-item-info">
            <span class="opinion-reaction-item-info__label">{{$t('opinion.veryGood')}}</span>
            <div class="importance">
              <div class="importance__icon"></div>
              <span class="importance__text">{{importanceTypeText}}</span>
            </div>
          </div>
        </li>
        <li class="opinion-reaction-item opinion-reaction-item--pretty-good"
            :class="activeOpinionLikeType('PRETTY_GOOD')"
        >
          <div class="opinion-reaction-item-info">
            <span class="opinion-reaction-item-info__label">{{$t('opinion.prettyGood')}}</span>
            <div class="importance">
              <div class="importance__icon"></div>
              <span class="importance__text">{{importanceTypeText}}</span>
            </div>
          </div>
        </li>
        <li class="opinion-reaction-item opinion-reaction-item--no-opinion"
            :class="activeOpinionLikeType('NO_OPINION')"
        >
          <div class="opinion-reaction-item-info">
            <span class="opinion-reaction-item-info__label">{{$t('opinion.noOpinion')}}</span>
            <div class="importance">
              <div class="importance__icon"></div>
              <span class="importance__text">{{importanceTypeText}}</span>
            </div>
          </div>
        </li>
        <li class="opinion-reaction-item opinion-reaction-item--pretty-bad"
            :class="activeOpinionLikeType('PRETTY_BAD')"
        >
          <div class="opinion-reaction-item-info">
            <span class="opinion-reaction-item-info__label">{{$t('opinion.prettyBad')}}</span>
            <div class="importance">
              <div class="importance__icon"></div>
              <span class="importance__text">{{importanceTypeText}}</span>
            </div>
          </div>
        </li>
        <li class="opinion-reaction-item opinion-reaction-item--very-bad"
            :class="activeOpinionLikeType('VERY_BAD')"
        >
          <div class="opinion-reaction-item-info">
            <span class="opinion-reaction-item-info__label">{{$t('opinion.veryBad')}}</span>
            <div class="importance">
              <div class="importance__icon"></div>
              <span class="importance__text">{{importanceTypeText}}</span>
            </div>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.opinion-list-item {
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
  padding: 21px 18px;
  position: relative;
  max-width: 600px;
  background-color: #fff;
  box-shadow: 0 2px 4px 0 rgba(79, 79, 79, .15);

  .opinion-header {
    display: inline-block;
    margin-bottom: 10px;
    font-size: 16px;
    font-weight: 600;
    line-height: normal;
    color: #7835a6;
    cursor: pointer;
  }

  .info-row {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
  }

  .info-label {
    margin-right: 5px;
    font-size: 12px;
    font-weight: 600;
    color: #4f4f4f;
  }

  .info-text {
    display: inline-block;
    margin-right: 5px;
    font-size: 12px;
    color: #4f4f4f;
  }

  .opinion-info {
    .opinion-reaction-list {
      display: flex;
      align-items: center;
      justify-content: space-around;
      width: 100%;
      padding: 0 2px;
      border-radius: 22.5px;
      background-color: #f2f2f2;

      .opinion-reaction-item {
        width: 42px;
        height: 42px;
        position: relative;
        background-size: 30px !important;
        background-position: center !important;
        background-repeat: no-repeat;

        &--very-good {
          background-image: url("~@/assets/img/opinion/very_good.svg");
        }
        &--pretty-good {
          background-image: url("~@/assets/img/opinion/pretty_good.svg");
        }
        &--no-opinion {
          background-image: url("~@/assets/img/opinion/no_opinion.svg");
        }
        &--pretty-bad {
          background-image: url("~@/assets/img/opinion/pretty_bad.svg");
        }
        &--very-bad {
          background-image: url("~@/assets/img/opinion/very_bad.svg");
        }
        &--more {
          background-image: url("~@/assets/img/opinion/emoji-more.svg");
        }

        &.active {
          width: 165px;
          height: 46px;
          padding-left: 45px;
          border-radius: 36px;
          box-shadow: 0 3px 9px 0 rgba(79, 79, 79, 0.12);
          background-color: #fff;
          background-position: 9px !important;

          .opinion-reaction-item-info {
            display: flex;
          }
        }

        .opinion-reaction-item-info {
          display: none;
          align-items: center;
          height: 100%;
          &__label {
            margin-right: 5px;
            font-size: 14px;
            font-weight: 600;
            color: #4f4f4f;
          }
        }

        .importance {
          display: flex;
          align-items: center;
          &__icon {
            width: 11px;
            height: 10px;
            margin: 0 3px 0 1px;
            background: url("~@/assets/img/opinion/sprite-heart.svg") no-repeat;
            background-position: -17px -67px;
          }
          &__text {
            font-size: 12px;
            font-weight: 600;
            color: #e60907;
          }
        }

      }
    }

  }

}
</style>